import { ComponentProps, Ref, forwardRef, MouseEvent } from 'react';
import { TextField, InputAdornment, IconButton, Box } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from '@mui/icons-material/Clear';
import useSandboxes from 'SandboxModule/hooks/useSandboxes';
import { Realm } from 'types';

const RealmSelector = (
  {
    onClick,
    showWarningLabel,
    realmId,
    tooltip,
    onClear,
    disabled,
  }: {
    onClick: ComponentProps<'div'>['onClick'];
    showWarningLabel?: boolean;
    tooltip?: string;
    realmId: Realm['realm_id'];
    onClear?: () => void;
    /**
     * GLX-1922: optional so the six existing callsites are unaffected. MUI puts
     * `disabled` on the inner <input>, but this component's onClick sits on the
     * TextField root - so clicks on the label or the dropdown adornment would
     * still open the realm menu. Both have to be suppressed.
     */
    disabled?: boolean;
  },
  ref?: Ref<HTMLDivElement>,
) => {
  const { getSandbox } = useSandboxes();

  const sandbox = getSandbox(realmId);

  const value = sandbox ? sandbox.realm_id : '';

  const handleClear = (e: MouseEvent) => {
    e.stopPropagation();
    onClear?.();
  };

  return (
    <TextField
      variant="outlined"
      label="Realm ID"
      value={value}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      inputRef={ref as Ref<HTMLInputElement>}
      helperText={tooltip}
      slotProps={{
        input: {
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {value && onClear && !disabled && (
                  <IconButton size="small" onClick={handleClear} sx={{ mr: -0.5 }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
                <ArrowDropDownIcon />
              </Box>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

export default forwardRef(RealmSelector);
